
// Self-contained safeFetch - works everywhere, no dependencies, no timing issues
async function safeFetch(url, options = {}, context = {}) {
  const config = {
    maxRetries: 3,
    retryDelay: 1000,
    timeout: 10000,
    retryOn: ['TypeError', 'NetworkError', 'TimeoutError'],
    logErrors: true,
    source: 'safe-fetch',
    ...options.fetchConfig
  };

  const fetchOptions = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache'
    },
    ...options
  };

  // Remove our custom config from fetch options
  delete fetchOptions.fetchConfig;

  for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
    try {
      // Check network connectivity
      if (!navigator.onLine) {
        throw new Error('No internet connection');
      }

      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), config.timeout);

      const response = await fetch(url, {
        ...fetchOptions,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Success - return response
      return response;

    } catch (error) {
      const isLastAttempt = attempt === config.maxRetries;
      const shouldRetry = config.retryOn.some(errorType =>
        error.name === errorType ||
        error.message.includes(errorType.toLowerCase()) ||
        error.message.includes('fetch') ||
        error.message.includes('network') ||
        error.message.includes('aborted')
      );

      // Collect error context for logging
      const errorContext = {
        url,
        attempt: attempt + 1,
        maxRetries: config.maxRetries + 1,
        errorType: error.name,
        errorMessage: error.message,
        timestamp: new Date().toISOString(),
        userOnline: navigator.onLine,
        currentUrl: window.location.href,
        connectionType: navigator.connection?.effectiveType || 'unknown',
        connectionDownlink: navigator.connection?.downlink || 'unknown',
        customContext: context
      };

      // Detect third-party services in stack trace
      if (error.stack) {
        const thirdPartyServices = [];
        if (error.stack.includes('intelligems')) thirdPartyServices.push('Intelligems A/B Testing');
        if (error.stack.includes('datadog')) thirdPartyServices.push('Datadog RUM');
        if (error.stack.includes('ajaxRequestInterceptor')) thirdPartyServices.push('Ajax Interceptor');
        errorContext.thirdPartyServices = thirdPartyServices;
      }

      // Log error details
      if (config.logErrors) {
        if (isLastAttempt) {
          console.group(`🚨 Fetch Failed After All Retries - ${config.source}`);
        } else {
          console.group(`⚠️ Fetch Attempt ${attempt + 1} Failed - ${config.source}`);
        }

        console.error('Error:', error);
        Object.entries(errorContext).forEach(([key, value]) => {
          if (key !== 'customContext' || (typeof value === 'object' && Object.keys(value).length > 0)) {
            console.log(`${key}:`, value);
          }
        });
        console.groupEnd();
      }

      // Send to Datadog if available
      if (window.DD_RUM && typeof window.DD_RUM.addError === 'function') {
        window.DD_RUM.addError(error, {
          context: errorContext,
          source: config.source
        });
      }

      // Retry logic
      if (!isLastAttempt && shouldRetry) {
        const delay = config.retryDelay * Math.pow(2, attempt); // Exponential backoff
        console.debug(`Retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }

      // Final failure
      throw error;
    }
  }
}


// Utility function to safely track events
async function safeTrackEvent(eventCategory, eventName, detail = {}) {
  try {
    // Wait for trackEvent with independent timeout
    const isTrackEventAvailable = () => typeof window.trackEvent === 'function';

    const trackEventAvailable = await new Promise((resolve) => {
      if (isTrackEventAvailable()) {
        resolve(true);
        return;
      }

      let attempts = 0;
      const maxAttempts = 30;

      const checkFunction = () => {
        attempts++;
        if (isTrackEventAvailable()) {
          clearInterval(check);
          resolve(true);
        } else if (attempts >= maxAttempts) {
          clearInterval(check);
          resolve(false);
        }
      };

      const check = setInterval(checkFunction, 100);
    });

    if (trackEventAvailable) {
      window.trackEvent(eventCategory, eventName, detail);
    } else {
      console.debug('🔍 Safe Track Event: trackEvent not available after waiting', eventName, detail);
    }
  } catch (error) {
    console.debug('🔍 Safe Track Event: error', eventName, detail, error.message);
  }
}

/**
 * URL Parameter Collection & Storage System
 * Separated into two functions: collect and store
 */

/**
 * Collects URL parameters from current page
 * @returns {object|null} - Parameter data object or null if no parameters
 */
function collectURLParams() {

  const params = new URLSearchParams(window.location.search);

  // Skip if no parameters
  if (params.toString() === '') {
    return null;
  }

  // Create entry with current data
  const paramData = {
    timestamp: new Date().toISOString(),
    url: window.location.href,
    pathname: window.location.pathname,
    parameters: {}
  };

  // Collect all current URL parameters
  for (const [key, value] of params.entries()) {
    paramData.parameters[key] = value;
  }

  console.debug('URL Parameters collected:', paramData.parameters);
  return paramData;
}

/**
 * Stores parameter data to localStorage with smart merging
 * @param {object} paramData - Parameter data from collectURLParams()
 */
async function storeURLParams(paramData = null) {
  if (!paramData) {
    paramData = collectURLParams();
  }

  if (!paramData) {
    return;
  }

  try {
    // Get existing URL_Params from localStorage
    let existingParams = {};
    try {
      const stored = localStorage.getItem('URL_Params');
      if (stored) {
        existingParams = JSON.parse(stored);
      }
    } catch (e) {
      console.warn('Error parsing existing URL_Params:', e);
      existingParams = {};
    }

    // Initialize structure if needed
    if (!existingParams.history) {
      existingParams.history = [];
    }
    if (!existingParams.latest) {
      existingParams.latest = {};
    }

    // Update latest parameters (merge with existing)
    Object.assign(existingParams.latest, paramData.parameters);

    // Add to history (keep last 50 entries to prevent storage bloat)
    existingParams.history.unshift(paramData);
    if (existingParams.history.length > 50) {
      existingParams.history = existingParams.history.slice(0, 50);
    }

    // Update metadata
    existingParams.lastUpdated = paramData.timestamp;
    existingParams.totalVisits = (existingParams.totalVisits || 0) + 1;

    // Store back to localStorage
    localStorage.setItem('URL_Params', JSON.stringify(existingParams));

    console.debug('URL Parameters stored successfully');

  } catch (error) {
    console.error('Error storing URL parameters:', error);
  }
}

/**
 * Gets a specific URL parameter value from stored URL parameters
 * @param {string} key - The parameter key to look for
 * @returns {string|null} - The parameter value or null if not found
 */
function getURLParam(key) {
  try {
    if (!key || typeof key !== 'string') {
      return null;
    }
    // Get URL_Params from localStorage (collected by our URL parameter system)
    const urlParamsData = localStorage.getItem('URL_Params');
    if (!urlParamsData) {
      return null;
    }

    const parsedData = JSON.parse(urlParamsData);
    const paramValue = parsedData.latest?.[key];

    console.debug('getURLParam: Looking for key:', key, '→ Found:', paramValue);
    return paramValue || null;

  } catch (error) {
    console.error('getURLParam: Error getting URL parameter:', error);
    return null;
  }
}

/**
 * Sets the user flow value in localStorage and tracks the event
 * @param {string} flowValue - The flow value to set (e.g., '28days', '1428')
 */
function setUserFlow(flowValue) {
  try {
    if (!flowValue || typeof flowValue !== 'string') {
      console.error('setUserFlow: Invalid flow value provided', flowValue);
      return false;
    }

    const normalizedValue = flowValue.toLowerCase();
    const previousValue = getUserFlow();

    localStorage.setItem('test_flow', normalizedValue);
    sessionStorage.setItem('test_flow', normalizedValue);
    document.cookie = `test_flow=${normalizedValue}; path=/; max-age=31536000; SameSite=Lax`;

    safeTrackEvent('funnel', 'set_flow', {
      new_flow: normalizedValue,
      previous_flow: previousValue,
    });

    return true;

  } catch (error) {
    console.error('setUserFlow: Error setting user flow', error);

    return false;
  }
}

/**
 * Gets the current user flow value from localStorage with URL parameter fallback
 * @returns {string|null} The current flow value or null if not set/error
 */
function getUserFlow() {
  try {
    let flowValue = localStorage.getItem('test_flow');
    if (flowValue === "1428") {
      flowValue = "28days"
    }

    if (flowValue && typeof flowValue === 'string' && flowValue.trim()) {
      // Check if the value is JSON-stringified (starts and ends with quotes)
      if (flowValue.startsWith('"') && flowValue.endsWith('"')) {
        try {
          flowValue = JSON.parse(flowValue);
        } catch {
          // If parsing fails, use the raw value
        }
      }
      return flowValue;
    }

    // Fallback to URL parameter
    const urlFlow = getURLParam('Flow');
    if (urlFlow && typeof urlFlow === 'string' && urlFlow.trim()) {
      return urlFlow.toLowerCase();
    }

    return null;

  } catch (error) {
    console.error('getUserFlow: Error getting user flow', error);
    return null;
  }
}

/**
 * Synchronizes multiple keys between localStorage and local values
 * Checks every 100ms:
 * - If localStorage has the key: reads it and updates local value
 * - If localStorage doesn't have the key: writes local value to localStorage (refresh)
 * @param {string|string[]} keys - Key or list of keys to track
 * @returns {object} - Control object with methods: start(), stop(), getKeys(), isRunning()
 */
function createRefreshFlagSync(keys = []) {
  const CHECK_INTERVAL = 100;

  if (!window._refreshFlagSyncState) {
    window._refreshFlagSyncState = {
      trackedKeys: new Set(),
      localValues: new Map(),
      intervalId: null,
      isRunning: false
    };
  }

  const state = window._refreshFlagSyncState;

  const keysArray = Array.isArray(keys) ? keys : (keys ? [keys] : []);
  keysArray.forEach(key => {
    if (key && !state.trackedKeys.has(key)) {
      state.trackedKeys.add(key);
      const storedValue = localStorage.getItem(key);
      if (storedValue !== null) {
        state.localValues.set(key, storedValue);
      }
    }
  });

  function syncRefreshFlags() {
    try {
      state.trackedKeys.forEach(key => {
        const storedValue = localStorage.getItem(key);

        if (storedValue !== null) {
          state.localValues.set(key, storedValue);
        } else if (state.localValues.has(key)) {
          const currentValue = state.localValues.get(key);
          localStorage.setItem(key, currentValue);
        }
      });
    } catch (error) {
      console.debug('refreshFlagSync: Error syncing flags', error);
    }
  }

  return {
    start() {
      if (state.isRunning) {
        return;
      }

      syncRefreshFlags();
      state.intervalId = setInterval(syncRefreshFlags, CHECK_INTERVAL);
      state.isRunning = true;
    },

    stop() {
      if (state.intervalId) {
        clearInterval(state.intervalId);
        state.intervalId = null;
        state.isRunning = false;
      }
    },

    getKeys() {
      return Array.from(state.trackedKeys);
    },

    isRunning() {
      return state.isRunning;
    }
  };
}

/**
 * Generates a UUID v4
 * @returns {string} UUID v4 string
 */
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Generates a unique order line ID using UUID
 * @returns {string} Unique identifier for order line
 */
function generateOrderLineId() {
  return generateUUID();
}

/**
 * Adds item to Shopify cart with order_line_id tracking
 * @param {string|number} id - Variant ID
 * @param {number} quantity - Quantity to add (default: 1)
 * @param {object} properties - Line item properties (default: {})
 * @param {string} [sellingPlan] - Optional selling plan ID
 * @returns {Promise<Response>} Fetch response
 */
async function addToCart(id, quantity = 1, properties = {}, sellingPlan = null) {
  if (!id) {
    throw new Error('Invalid cart data: missing variant id');
  }

  const addData = {
    id: id,
    quantity: quantity,
    properties: {
      ...properties,
      _order_line_id: generateOrderLineId()
    }
  };

  if (sellingPlan) {
    addData.selling_plan = sellingPlan;
  }

  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(addData)
  });

  return response;
}

/**
 * Refreshes the cart by dispatching a custom event and updating the cart drawer if available
 * @returns {void}
 */
function refreshCart() {
  try {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true }));
  } catch (error) {
    console.error('Error dispatching cart refresh event:', error);
  }
  
  if (typeof window.updateCartDrawer === 'function') {
    try {
      window.updateCartDrawer();
    } catch (error) {
      console.error('Error updating cart drawer:', error);
    }
  }
}

/**
 * Extracts the last character from a value (string or array) 
 * @param {string|array} value - The value to extract from
 * @returns {string|array|null} Last character(s) or null
 */
function extractLastChar(value) {
  if (!value) return null;
  
  if (Array.isArray(value)) {
    return value.map(item => {
      if (typeof item === 'string' && item.length > 0) {
        return item.charAt(item.length - 1);
      }
      return null;
    }).filter(item => item !== null);
  }
  
  if (typeof value === 'string' && value.length > 0) {
    return value.charAt(value.length - 1);
  }
  
  return null;
}

/**
 * Extracts the answer without the last character (string or array)
 * @param {string|array} value - The value to extract from
 * @returns {string|array|null} Answer without last character(s) or null
 */
function extractAnswerWithoutLastChar(value) {
  if (!value) return null;
  
  if (Array.isArray(value)) {
    return value.map(item => {
      if (typeof item === 'string' && item.length > 0) {
        return item.slice(0, -1);
      }
      return null;
    }).filter(item => item !== null);
  }
  
  if (typeof value === 'string' && value.length > 0) {
    return value.slice(0, -1);
  }
  
  return null;
}

/**
 * Checks if a value (string or array) contains any of the given codes
 * @param {string|array} v - The value to check
 * @param {...string} codes - The codes to check for
 * @returns {boolean} True if the value contains any of the given codes, false otherwise
 */
function hasAny(v, ...codes) {
  if (!v) return false;
  if (Array.isArray(v)) {
    return codes.some((code) => v.includes(code));
  }
  return codes.includes(v);
}

// Make all functions explicitly global
window.safeFetch = safeFetch;
window.safeTrackEvent = safeTrackEvent;
window.collectURLParams = collectURLParams;
window.storeURLParams = storeURLParams;

// Auto-save URL params on page load
storeURLParams();
window.getURLParam = getURLParam;
window.setUserFlow = setUserFlow;
window.getUserFlow = getUserFlow;
window.createRefreshFlagSync = createRefreshFlagSync;
window.generateUUID = generateUUID;
window.generateOrderLineId = generateOrderLineId;
window.addToCart = addToCart;
window.refreshCart = refreshCart;
window.extractLastChar = extractLastChar;
window.extractAnswerWithoutLastChar = extractAnswerWithoutLastChar;
window.hasAny = hasAny;
